import moment from 'moment';
import { createContext, useContext, useEffect, useState } from 'react';
import useBillingActions from '../hooks/APIHooks/finance/useBillingActions';
import { BillingHistory } from '../types/history';
import { User } from '../types/users';
import { useAuthContext } from './AuthProvider';

// datatype of the context declared here
interface IBillingHistoryContext {
  billingHistoryList: Array<BillingHistory>;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}

// creating the context here
const BillingHistoryContext = createContext<IBillingHistoryContext | null>(
  null
);

const BillingHistoryProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // integration of react hooks here
  const [billingHistoryList, setBillingHistoryList] = useState<
    Array<BillingHistory>
  >([]);

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { getBillingInfoListHandler, getBillingInfoByAssociateIdHandler } =
    useBillingActions(
      (currentUser as User)?.role_name === 'Admin'
        ? 'billingInfoList'
        : 'billingInfoByAssociateId',
      (currentUser as User)?.role_name === 'Associate'
        ? currentUser?.id
        : undefined
    );

  // setting values into local states here
  useEffect(() => {
    setBillingHistoryList([]);

    if ((currentUser as User)?.role_name === 'Admin') {
      if (getBillingInfoListHandler.data?.is_success) {
        setBillingHistoryList(
          getBillingInfoListHandler.data.data.map((bill) => ({
            ...bill,
            created_at: moment(bill.created_at).format('DD-MM-YYYY'),
          }))
        );
      }
    } else if ((currentUser as User)?.role_name === 'Associate') {
      if (getBillingInfoByAssociateIdHandler.data?.is_success) {
        setBillingHistoryList(
          getBillingInfoByAssociateIdHandler.data.data.map((bill) => ({
            ...bill,
            created_at: moment(bill.created_at).format('DD-MM-YYYY'),
          }))
        );
      }
    }
  }, [
    currentUser,
    getBillingInfoListHandler.data,
    getBillingInfoByAssociateIdHandler.data,
  ]);

  // rendering the billing history provider here
  return (
    <BillingHistoryContext.Provider
      value={{
        billingHistoryList,
        isError:
          (currentUser as User)?.role_name === 'Admin'
            ? getBillingInfoListHandler.isError
            : getBillingInfoByAssociateIdHandler.isError,
        isLoading:
          (currentUser as User)?.role_name === 'Admin'
            ? getBillingInfoListHandler.isLoading
            : getBillingInfoByAssociateIdHandler.isLoading,
        isSuccess:
          (currentUser as User)?.role_name === 'Admin'
            ? getBillingInfoListHandler.isSuccess
            : getBillingInfoByAssociateIdHandler.isSuccess,
      }}
    >
      {children}
    </BillingHistoryContext.Provider>
  );
};

export default BillingHistoryProvider;

// custom hook to access billing history context declared here
export const useBillingHistoryContext = () => {
  const context = useContext(BillingHistoryContext);

  if (!context) {
    throw new Error(
      'useBillingHistoryContext must be within BillingHistoryProvider!'
    );
  }

  return context;
};
